<template>
  <Modal :show="show" @close="handleCancel" size="sm">
    <template #header>
      <div class="flex items-center">
        <div
          v-if="variant === 'danger'"
          class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/20"
        >
          <svg class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div
          v-else-if="variant === 'warning'"
          class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/20"
        >
          <svg class="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div
          v-else-if="variant === 'info'"
          class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/20"
        >
          <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 class="ml-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ title }}
        </h3>
      </div>
    </template>
    <template #default>
      <div class="space-y-4">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ message }}
        </p>

        <slot />

        <div class="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            @click="handleCancel"
            :disabled="processing"
          >
            {{ cancelText }}
          </Button>
          <Button
            type="button"
            :variant="computedConfirmVariant"
            @click="handleConfirm"
            :disabled="processing"
          >
            <svg v-if="processing" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ processing ? processingText : confirmText }}
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from 'vue';
import Modal from './Modal.vue';
import Button from './Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Confirm Action',
  },
  message: {
    type: String,
    default: 'Are you sure you want to proceed?',
  },
  confirmText: {
    type: String,
    default: 'Confirm',
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  processingText: {
    type: String,
    default: 'Processing...',
  },
  variant: {
    type: String,
    default: 'danger',
    validator: (value) => ['danger', 'warning', 'info', 'primary'].includes(value),
  },
  confirmVariant: {
    type: String,
    default: null,
  },
  processing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

/**
 * Get confirm button variant based on modal variant
 */
const computedConfirmVariant = computed(() => {
  if (props.confirmVariant) return props.confirmVariant;
  return props.variant;
});

/**
 * Handle confirm action
 */
const handleConfirm = () => {
  if (!props.processing) {
    emit('confirm');
  }
};

/**
 * Handle cancel action
 */
const handleCancel = () => {
  if (!props.processing) {
    emit('cancel');
  }
};
</script>
